import type { SessionEvent } from '@greenroom/shared'
import { clsx } from 'clsx'
import {
  CircleDot,
  CirclePause,
  CirclePlay,
  CircleStop,
  HardDrive,
  LogIn,
  LogOut,
  MonitorOff,
  MonitorUp,
  ShieldCheck,
  TriangleAlert,
  UserX,
  Wifi,
  WifiOff,
  type LucideIcon,
} from 'lucide-react'
import { formatClock } from '../lib/format'

type Tone = 'neutral' | 'ok' | 'warn' | 'danger' | 'rec'
type Described = { text: string; tone: Tone; icon: LucideIcon }

const seconds = (ms: unknown) => (typeof ms === 'number' ? ` after ${Math.max(1, Math.round(ms / 1000))} s` : '')

/** One wording for session events, used by the live activity log and the recording page. */
export function describeEvent(e: Pick<SessionEvent, 'type' | 'participantName' | 'data'>): Described {
  const who = e.participantName ?? 'Someone'
  switch (e.type) {
    case 'participant_joined':
      return { text: `${who} joined`, tone: 'ok', icon: LogIn }
    case 'participant_left':
    case 'left':
      return { text: `${who} left`, tone: 'neutral', icon: LogOut }
    case 'participant_dropped':
      return { text: `${who} dropped out`, tone: 'danger', icon: WifiOff }
    case 'connection_lost':
      return { text: `${who} lost the connection`, tone: 'warn', icon: WifiOff }
    case 'connection_restored':
      return { text: `${who} reconnected${seconds(e.data.durationMs)}`, tone: 'ok', icon: Wifi }
    case 'recording_started':
      return { text: `${who} started recording`, tone: 'rec', icon: CircleDot }
    case 'recording_paused':
      return { text: `${who} paused the recording`, tone: 'warn', icon: CirclePause }
    case 'recording_resumed':
      return { text: `${who} resumed the recording`, tone: 'rec', icon: CirclePlay }
    case 'recording_stopped':
      return { text: `${who} stopped the recording`, tone: 'neutral', icon: CircleStop }
    case 'screen_share_started':
      return { text: `${who} started sharing a screen`, tone: 'neutral', icon: MonitorUp }
    case 'screen_share_stopped':
      return { text: `${who} stopped sharing a screen`, tone: 'neutral', icon: MonitorOff }
    case 'participant_removed':
      return { text: `${who} was removed${typeof e.data.by === 'string' ? ` by ${e.data.by}` : ''}`, tone: 'danger', icon: UserX }
    case 'role_changed':
      return {
        text: `${who} is now ${e.data.role === 'producer' ? 'a producer' : 'a guest'}${typeof e.data.by === 'string' ? ` (by ${e.data.by})` : ''}`,
        tone: 'neutral',
        icon: ShieldCheck,
      }
    case 'recording_error':
      return { text: `${who}: recording problem${typeof e.data.message === 'string' ? `: ${e.data.message}` : ''}`, tone: 'danger', icon: TriangleAlert }
    case 'storage_low':
      return { text: `${who} is running out of disk space`, tone: 'warn', icon: HardDrive }
    default:
      return { text: `${who}: ${e.type.replaceAll('_', ' ')}`, tone: 'neutral', icon: CircleDot }
  }
}

const TONE: Record<Tone, string> = {
  neutral: 'bg-surface-3 text-muted',
  ok: 'bg-ok-soft text-ok',
  warn: 'bg-warn-soft text-warn',
  danger: 'bg-danger-soft text-danger',
  rec: 'bg-rec-soft text-rec',
}

export function EventList({ events, empty = 'Nothing happened yet.' }: { events: Pick<SessionEvent, 'id' | 'at' | 'type' | 'participantName' | 'data'>[]; empty?: string }) {
  if (events.length === 0) return <p className="text-sm text-muted">{empty}</p>
  return (
    <ol className="space-y-2.5">
      {events.map((e) => {
        const d = describeEvent(e)
        const Icon = d.icon
        return (
          <li key={e.id} className="flex items-start gap-3 text-sm">
            <span className={clsx('mt-px flex size-6 shrink-0 items-center justify-center rounded-full', TONE[d.tone])}>
              <Icon className="size-3.5" />
            </span>
            <span className="min-w-0 flex-1 pt-0.5">{d.text}</span>
            <time className="shrink-0 pt-0.5 font-mono text-xs text-faint tabular-nums">{formatClock(e.at)}</time>
          </li>
        )
      })}
    </ol>
  )
}
